import { debounce } from '../core/helpers';

const getAccordionHeadersByBreakpoint = (currentHeader, breakpoint) => {
  const accordion = currentHeader.closest('.accordion');
  if (!accordion) {
    return [];
  }

  return Array.from(
    accordion.querySelectorAll(`[data-${breakpoint}-accordion] [data-accordion-title]`)
  ).filter((header) => {
    const item = header.closest(`[data-${breakpoint}-accordion]`);
    return item && !item.classList.contains('visually-hidden');
  });
};

const focusAccordionHeaderByOffset = (currentHeader, breakpoint, offset) => {
  const headers = getAccordionHeadersByBreakpoint(currentHeader, breakpoint);
  const currentIndex = headers.indexOf(currentHeader);
  if (currentIndex === -1 || headers.length === 0) {
    return;
  }

  const nextIndex = (currentIndex + offset + headers.length) % headers.length;
  headers[nextIndex].focus();
};

const focusAccordionBoundaryHeader = (currentHeader, breakpoint, target) => {
  const headers = getAccordionHeadersByBreakpoint(currentHeader, breakpoint);
  if (headers.length === 0) {
    return;
  }

  if (target === 'first') {
    headers[0].focus();
    return;
  }

  headers[headers.length - 1].focus();
};

const openCloseAccordion = (item, breakpoint) => {
  try {
    let current_breakpoint = window.theme.breakpoints.current;
    const accordion = item.closest('.accordion');

    if (current_breakpoint !== 'mobile') {
      current_breakpoint = 'desktop';
    }

    if (!accordion.querySelector(`[data-${breakpoint}-accordion]`) || current_breakpoint !== breakpoint) {
      return false;
    }

    let is_active = item.classList.contains('active');
    const is_active_on_load = item.classList.contains('active-on-load');
    const content = item.querySelector('[data-accordion-content]');
    const allActive = accordion.getAttribute('data-all-active') === 'true';

    if (!allActive) {
      accordion.querySelectorAll(`[data-${breakpoint}-accordion]`).forEach(el => {
        el.classList.remove('active', 'active-on-load');
      });
    } else {
      item.classList.remove('active-on-load');
      if (is_active) {
        item.classList.remove('active');
      }
    }

    if (is_active_on_load && !allActive) {
      is_active = false;
    }

    if (!is_active) {
      item.classList.add('active');
      if (content) content.style.maxHeight = content.scrollHeight + 'px';
    }

    accordion.querySelectorAll(`[data-${breakpoint}-accordion]:not(.active) [data-accordion-content]`).forEach(el => {
      el.style.maxHeight = 0;
    });

    // [ADA] Update aria attributes for all items
    accordion.querySelectorAll(`[data-${breakpoint}-accordion]`).forEach(accordionItem => {
      const accordionTitle = accordionItem.querySelector('[data-accordion-title]');
      const accordionContent = accordionItem.querySelector('[data-accordion-content]');
      const isActive = accordionItem.classList.contains('active');

      if (accordionTitle) {
        accordionTitle.setAttribute('aria-expanded', isActive);
        accordionTitle.setAttribute('role', 'button');
      }
      if (accordionContent) {
        accordionContent.setAttribute('aria-hidden', !isActive);
        accordionContent.setAttribute('role', 'region');
        accordionContent.setAttribute('tabindex', isActive ? 0 : -1);
        accordionContent.style.visibility = isActive ? 'visible' : 'hidden';

        if (accordionTitle) {
          accordionContent.setAttribute('aria-labelledby', accordionTitle.id);
        }
      }
    });

    window.dispatchEvent(new Event('resize'));
  } catch (e) {
    console.log(e);
  }

  return true;
};

/*
const resetAccordion = (accordion_item = '') => {
  const parent_element = (accordion_item === '') ? '' : `${accordion_item} `;

  if (window.theme.breakpoints.current === 'mobile') {
    $(`${parent_element}[data-desktop-accordion]`).removeClass('active');
    $(`${parent_element}[data-desktop-accordion] [data-accordion-content]`).removeAttr('style');
    if ($(`${accordion_item}.has-accordion .accordion [data-mobile-accordion]`).length) {
      $(`${accordion_item}.has-accordion .accordion [data-mobile-accordion]`).first().addClass('active-on-load');
    }
    if ($(`${accordion_item}.has-accordion .accordion [data-desktop-accordion]`).length) {
      $(`${accordion_item}.has-accordion .accordion [data-desktop-accordion]`).first().addClass('active-on-load');
    }
    if ($(`${parent_element}[data-mobile-accordion].active-on-load`).length) {
      $(`${parent_element}[data-mobile-accordion].active-on-load`).trigger('click');
    }
  } else {
    $(`${parent_element}[data-mobile-accordion]`).removeClass('active');
    $(`${parent_element}[data-mobile-accordion] [data-accordion-content]`).removeAttr('style');
    $(`${parent_element}.has-accordion .accordion [data-mobile-accordion]`).first().addClass('active-on-load');
    $(`${parent_element}.has-accordion .accordion [data-desktop-accordion]`).first().addClass('active-on-load');
    $(`${parent_element}[data-desktop-accordion].active-on-load`).trigger('click');
  }
};
*/

// Content height can change when resizing the browser
const updateActiveHeight = (accordion_item = '') => {
  const parent_element = accordion_item ? document.querySelector(accordion_item) : document;
  const active_item = parent_element.querySelector('.active');
  if (!active_item) return;
  const content = active_item.querySelector('[data-accordion-content]');
  if (content) content.style.maxHeight = content.scrollHeight + 'px';
};

const initAccordion = (accordion_item = '') => {
  // accordion_item can be a string or a querySelector element

  let parent_element = accordion_item;
  if (typeof accordion_item === 'string') {
    parent_element = accordion_item ? document.querySelector(accordion_item) : document;
  }

  if (!parent_element) {
    console.error('Accordion element not found');
    return;
  }

  const applyADA = () => {
    parent_element.querySelectorAll('.accordion__item').forEach(accordionItem => {
      const accordionTitle = accordionItem.querySelector('[data-accordion-title]');
      const accordionContent = accordionItem.querySelector('[data-accordion-content]');
      const isActive = accordionItem.classList.contains('active') || accordionItem.classList.contains('active-on-load');
      const isDesktopAccordion = accordionItem.hasAttribute('data-desktop-accordion');
      const isMobileAccordion = accordionItem.hasAttribute('data-mobile-accordion');
      const current_breakpoint = window.theme.breakpoints.current;

      // Only hide if it's an accordion at the current breakpoint
      let shouldApplyADA = false;
      if (current_breakpoint === 'mobile' && isMobileAccordion) {
        shouldApplyADA = true;
      } else if (current_breakpoint !== 'mobile' && isDesktopAccordion) {
        shouldApplyADA = true;
      }

      if (shouldApplyADA) {
        if (accordionTitle) {
          accordionTitle.setAttribute('aria-expanded', isActive);
          accordionTitle.setAttribute('role', 'button');
        }
        if (accordionContent) {
          accordionContent.setAttribute('aria-hidden', !isActive);
          accordionContent.setAttribute('role', 'region');
          accordionContent.setAttribute('tabindex', isActive ? 0 : -1);
          accordionContent.style.visibility = isActive ? 'visible' : 'hidden';

          if (accordionTitle && accordionTitle.id) {
            accordionContent.setAttribute('aria-labelledby', accordionTitle.id);
          }
        }
      } else {
        // Restore default visibility for non-accordion state
        if (accordionContent) {
          accordionContent.removeAttribute('aria-hidden');
          accordionContent.removeAttribute('role');
          accordionContent.removeAttribute('tabindex');
          accordionContent.style.visibility = '';
          accordionContent.style.maxHeight = '';
          accordionContent.style.opacity = '';
        }
        if (accordionTitle) {
          accordionTitle.removeAttribute('aria-expanded');
          // accordionTitle.removeAttribute('role'); 
        }
      }
    });
  };

  applyADA();

  window.addEventListener('breakpointHasChanged', () => {
    applyADA();
  });

  // Mobile click/touch
  parent_element.querySelectorAll('.has-accordion .accordion [data-mobile-accordion] [data-accordion-title]').forEach(el => {
    el.addEventListener('click', (e) => {
      if (window.theme.breakpoints.current !== 'mobile') return;
      e.preventDefault();
      e.stopPropagation();
      openCloseAccordion(el.closest('[data-mobile-accordion]'), 'mobile');
    });

    el.addEventListener('keydown', (e) => {
      if (window.theme.breakpoints.current !== 'mobile') return;

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        focusAccordionHeaderByOffset(el, 'mobile', 1);
        return;
      }

      if (e.key === 'ArrowUp') {
        e.preventDefault();
        focusAccordionHeaderByOffset(el, 'mobile', -1);
        return;
      }

      if (e.key === 'Home') {
        e.preventDefault();
        focusAccordionBoundaryHeader(el, 'mobile', 'first');
        return;
      }

      if (e.key === 'End') {
        e.preventDefault();
        focusAccordionBoundaryHeader(el, 'mobile', 'last');
        return;
      }

      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        e.stopPropagation();
        openCloseAccordion(el.closest('[data-mobile-accordion]'), 'mobile');
      }
    });
  });

  // Desktop click
  parent_element.querySelectorAll('.has-accordion .accordion [data-desktop-accordion] [data-accordion-title]').forEach(el => {
    el.addEventListener('click', (e) => {
      if (window.theme.breakpoints.current === 'mobile') return;
      e.preventDefault();
      e.stopPropagation();
      openCloseAccordion(el.closest('[data-desktop-accordion]'), 'desktop');
    });
    el.addEventListener('keydown', (e) => {
      if (window.theme.breakpoints.current === 'mobile') return;
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        focusAccordionHeaderByOffset(el, 'desktop', 1);
        return;
      }

      if (e.key === 'ArrowUp') {
        e.preventDefault();
        focusAccordionHeaderByOffset(el, 'desktop', -1);
        return;
      }

      if (e.key === 'Home') {
        e.preventDefault();
        focusAccordionBoundaryHeader(el, 'desktop', 'first');
        return;
      }

      if (e.key === 'End') {
        e.preventDefault();
        focusAccordionBoundaryHeader(el, 'desktop', 'last');
        return;
      }

      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        e.stopPropagation();
        openCloseAccordion(el.closest('[data-desktop-accordion]'), 'desktop');
      }
    });
  });

  window.addEventListener('resize', debounce(() => {
    updateActiveHeight(accordion_item);
  }, 100));

  // Open the "Active on Load" item(s) if defined
  parent_element.querySelectorAll('.accordion__item.active-on-load').forEach(el => {
    const accordion = el.closest('.accordion');
    if (accordion && accordion.getAttribute('data-all-active') === 'true') {
      el.classList.add('active');
      const content = el.querySelector('[data-accordion-content]');
      if (content) content.style.maxHeight = content.scrollHeight + 'px';
    } else {
      const title = el.querySelector('[data-accordion-title]');
      if (title) title.click();
    }
  });
};

export {
  initAccordion,
  // resetAccordion,
};
